import { Box, Stack, useMediaQuery } from "@mui/material";
import { LEAD_FIELDS } from "./Lead.config.jsx";
import LeadHeaderLeft from "./LeadHeader/LeadHeaderLeft.jsx";
import InlineFieldCard from "./LeadHeader/InlineFieldCard.jsx";
import { getStatusUI, STATUS_UI_MAP } from "../../config/leadStatus.js";
import { LayoutGroupContext } from "framer-motion";
import { useContext, useMemo } from "react";

const containerSx = {
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
};

const cardSx = {
  borderRadius: 3,
  bgcolor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  overflow: "hidden",
};

export default function LeadHeader({
  lead = {},
  updateLead,
  dynamicStatus = [],
  leadSource = [],
}) {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:900px)");
  const layoutCtx = useContext(LayoutGroupContext);
  const collapsed = Boolean(layoutCtx?.collapsed);

  const isCollapsedUI = collapsed || isTablet;

  const dynamicMap = useMemo(() => {
    // Start with all hardcoded statuses from STATUS_UI_MAP
    const map = {};

    // Add all statuses from STATUS_UI_MAP
    Object.keys(STATUS_UI_MAP).forEach((key) => {
      const ui = getStatusUI(key);
      map[key] = {
        key,
        label: ui.label || key,
        color: ui.color,
        icon: ui.icon,
      };
    });

    // Override with dynamicStatus from settings if available
    if (Array.isArray(dynamicStatus)) {
      dynamicStatus.forEach((s) => {
        const ui = getStatusUI(s.key);
        map[s.key] = {
          key: s.key,
          label: (s.label || s.key)
            .replace(/^lead\s+/i, "")
            .replace(/\b\w/g, (c) => c.toUpperCase()),
          color: ui.color,
          icon: ui.icon,
        };
      });
    }

    return map;
  }, [dynamicStatus]);

  const STATUS = dynamicMap;
  const statusKeys = Object.keys(STATUS);
  const currentKey =
    lead?.current_status?.key && STATUS[lead.current_status.key]
      ? lead.current_status.key
      : statusKeys[0];

  const statusObj = STATUS[currentKey] || {};

  const leadSourceOptions = useMemo(() => {
    return (Array.isArray(leadSource) ? leadSource : [])
      .filter((item) => item?.enabled)
      .map((item) => ({ label: item.label, value: item.key }));
  }, [leadSource]);

  const normalizeKey = (s) => String(s || "").trim().toLowerCase().replace(/[\s_]+/g, "_");

  const getFieldValue = (field) => {
    // Direct path lookup
    if (field.path) {
      const direct = field.path.split(".").reduce((obj, k) => obj?.[k], lead);
      if (direct) return direct;

      // For custom_fields paths, also check with normalized keys and meta_raw_field_data
      if (field.path.startsWith("custom_fields.")) {
        const targetKey = normalizeKey(field.path.split(".")[1]);
        const cf = lead?.custom_fields || {};

        // Check all custom_fields keys with normalization
        for (const [k, v] of Object.entries(cf)) {
          if (normalizeKey(k) === targetKey && v && typeof v === "string") return v;
        }

        // Check inside meta_raw_field_data array
        const raw = cf.meta_raw_field_data;
        if (Array.isArray(raw)) {
          const f = raw.find((item) => normalizeKey(item?.name) === targetKey);
          if (f?.values?.[0]) return String(f.values[0]).trim();
        }
      }
    }
    return lead?.[field.key];
  };

  const handleFieldSave = (field, newValue) => {
    if (field.path) {
      const customFieldKey = field.path.split(".")[1];
      updateLead("custom_fields", {
        ...(lead?.custom_fields || {}),
        [customFieldKey]: newValue,
      });
    } else {
      updateLead(field.key, newValue);
    }
  };

  return (
    <Box sx={containerSx}>
      <Stack
        direction={isCollapsedUI ? "column" : "row"}
        spacing={2}
        alignItems={isCollapsedUI ? "stretch" : "stretch"}
      >
        {/* Left card - Avatar, Name, Chips, Actions */}
        <Box
          sx={{ ...cardSx, minWidth: { xs: "100%", md: 320 }, flexShrink: 0 }}
        >
          <LeadHeaderLeft
            lead={lead}
            STATUS={STATUS}
            statusObj={statusObj}
            updateLead={updateLead}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        </Box>

        {/* Right section - Individual Field cards */}
        <Stack
          direction={isCollapsedUI ? "column" : "row"}
          spacing={2}
          sx={{
            flex: 1,
            width: isCollapsedUI ? "100%" : "auto",
          }}
        >
          {LEAD_FIELDS.map((field) => {
            const Icon = field.icon;
            const value = getFieldValue(field);

            return (
              <Box key={field.key} sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
                <InlineFieldCard
                  label={field.label.replace(" *", "")}
                  value={value}
                  icon={<Icon sx={{ fontSize: 18, color: field.iconColor }} />}
                  iconBg={field.bg}
                  isPhone={field.key === "contact"}
                  isEmail={field.key === "email"}
                  isSelect={field.key === "lead_source"}
                  options={field.key === "lead_source" ? leadSourceOptions : []}
                  onSave={(newValue) => handleFieldSave(field, newValue)}
                />
              </Box>
            );
          })}
        </Stack>
      </Stack>
    </Box>
  );
}
